import { useEffect, useId, useRef, useState } from 'react';

import type { LoadingStateType } from '@/shared/config';
import type {
  ToothBasisSeriesName,
  ToothMassCentroidMm,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { debug } from '@/shared/lib';

import {
  TOOTH_BASIS_SERIES_ORDER,
  type ToothBasisSeriesCacheEntry,
  type ToothBasisSeriesFrames,
  type ToothBasisSeriesView,
  type ToothExtentsProfiles,
  type ToothSliceGeometry,
  type ToothSliceOptionsBySeries,
} from '../config';
import {
  getCachedToothBasisSeriesEntry,
  loadToothBasisSeriesEntry,
  makeToothBasisSeriesCacheKey,
  makeToothBasisSeriesProjectionKeys,
  makeToothBasisSeriesSourceKey,
  markToothSliceCardSettled,
  registerToothSliceCard,
  releaseToothBasisSeriesEntry,
  subscribeToToothBasisSeriesEntry,
  unregisterToothSliceCard,
} from '../lib';

type UseToothBasisSeriesParams = {
  input: ToothSliceGeometry | null;
  isEnabled?: boolean;
  isPanelBlocking?: boolean;
  sliceOptions?: ToothSliceOptionsBySeries;
  spacingOutMm?: number;
};

type UseToothBasisSeriesResult = {
  frames: ToothBasisSeriesFrames | null;
  loading: LoadingStateType;
  massCentroidMm: ToothMassCentroidMm | null;
  pendingSeries: ToothBasisSeriesName[];
  profiles: ToothExtentsProfiles | null;
  series: ToothBasisSeriesView | null;
  slabThicknessMm: Record<ToothBasisSeriesName, number> | null;
  sliceStepMm: Record<ToothBasisSeriesName, number> | null;
};

export const useToothBasisSeries = (
  params: UseToothBasisSeriesParams,
): UseToothBasisSeriesResult => {
  const {
    input,
    isEnabled = true,
    isPanelBlocking = false,
    sliceOptions,
    spacingOutMm,
  } = params;

  const consumerID = useId();

  const [loading, setLoading] = useState<LoadingStateType>('idle');
  const [cachedEntry, setEntry] = useState<{
    sourceKey: string;
    projectionKeys: Record<ToothBasisSeriesName, string>;
    value: ToothBasisSeriesCacheEntry;
  } | null>(null);

  const cacheKey =
    input && isEnabled
      ? makeToothBasisSeriesCacheKey({ input, sliceOptions, spacingOutMm })
      : null;
  const sourceKey =
    input && isEnabled
      ? makeToothBasisSeriesSourceKey({ input, spacingOutMm })
      : null;
  const projectionKeys =
    input && isEnabled
      ? makeToothBasisSeriesProjectionKeys({
          input,
          sliceOptions,
          spacingOutMm,
        })
      : null;
  const inputRef = useRef(input);
  inputRef.current = input;

  const sliceOptionsRef = useRef(sliceOptions);
  sliceOptionsRef.current = sliceOptions;

  const registrationKey =
    input && isPanelBlocking
      ? makeToothBasisSeriesCacheKey({ input, sliceOptions, spacingOutMm })
      : null;

  useEffect(() => {
    if (!registrationKey) {
      return;
    }

    registerToothSliceCard(registrationKey);

    return () => unregisterToothSliceCard(registrationKey);
  }, [registrationKey]);

  useEffect(() => {
    const currentInput = inputRef.current;

    if (!cacheKey || !sourceKey || !currentInput) {
      setLoading('idle');
      setEntry(null);
      return;
    }

    const cached = getCachedToothBasisSeriesEntry(cacheKey);
    const projectionKeys = makeToothBasisSeriesProjectionKeys({
      input: currentInput,
      sliceOptions: sliceOptionsRef.current,
      spacingOutMm,
    });
    let isStale = false;

    const unsubscribeEntry = subscribeToToothBasisSeriesEntry({
      cacheKey,
      listener: (nextEntry) => {
        if (isStale) {
          return;
        }

        setEntry({ sourceKey, projectionKeys, value: nextEntry });
        setLoading('succeeded');
        markToothSliceCardSettled(cacheKey);
      },
    });

    if (cached) {
      setEntry({ sourceKey, projectionKeys, value: cached });
      setLoading('succeeded');
      markToothSliceCardSettled(cacheKey);

      return () => {
        isStale = true;
        unsubscribeEntry();
      };
    }

    setLoading('pending');

    loadToothBasisSeriesEntry({
      cacheKey,
      consumerID,
      input: currentInput,
      sliceOptions: sliceOptionsRef.current,
      spacingOutMm,
    })
      .then((nextEntry) => {
        if (isStale) {
          return;
        }

        setEntry({ sourceKey, projectionKeys, value: nextEntry });
        setLoading('succeeded');
        markToothSliceCardSettled(cacheKey);
      })
      .catch((error: unknown) => {
        if (isStale) {
          return;
        }

        debug('[FullHeadMPR] Tooth-basis series failed', error);
        setLoading('failed');
        markToothSliceCardSettled(cacheKey);
      });

    return () => {
      isStale = true;
      unsubscribeEntry();
      releaseToothBasisSeriesEntry({ cacheKey, consumerID });
    };
  }, [cacheKey, sourceKey, consumerID, spacingOutMm]);

  const entry =
    cachedEntry?.sourceKey === sourceKey ? cachedEntry?.value : null;
  const pendingSeries = projectionKeys
    ? TOOTH_BASIS_SERIES_ORDER.filter(
        (name) => cachedEntry?.projectionKeys[name] !== projectionKeys[name],
      )
    : [];

  return {
    frames: entry?.frames ?? null,
    loading:
      loading === 'succeeded' && pendingSeries.length ? 'pending' : loading,
    massCentroidMm: entry?.massCentroidMm ?? null,
    pendingSeries,
    profiles: entry?.profiles ?? null,
    series: entry?.view ?? null,
    slabThicknessMm: entry?.slabThicknessMm ?? null,
    sliceStepMm: entry?.sliceStepMm ?? null,
  };
};
